import { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import ErrorModal from '../../components/ErrorModal';
import { ApiError } from '../../services/http';

interface OrderItem {
  id: string;
  name: string;
  quantity: number;
  price: number;
  specialInstructions?: string;
}

interface Order {
  id: string;
  tableNumber: string;
  customerName: string;
  waiterName?: string;
  items: OrderItem[];
  totalAmount: number;
  status: 'pending' | 'preparing' | 'ready' | 'delivered' | 'cancelled';
  createdAt: string;
  specialNotes?: string;
}

export default function OrdersPage() {
  const { user } = useAuth();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [errorModal, setErrorModal] = useState<{ open: boolean; title?: string; message: string; details?: string }>({ open: false, message: '' });

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    if (!user?.uid) return;
    
    try {
      setLoading(true);
      console.log('📋 Cargando órdenes para usuario:', user.uid);
      
      // Mock data por ahora
      const mockOrders: Order[] = [
        {
          id: '1',
          tableNumber: '5',
          customerName: 'Juan Pérez',
          waiterName: 'María García',
          items: [
            { id: '1', name: 'Pizza Margherita', quantity: 2, price: 12.99 },
            { id: '2', name: 'Ensalada César', quantity: 1, price: 8.50 }
          ],
          totalAmount: 34.48,
          status: 'pending',
          createdAt: new Date().toISOString(),
          specialNotes: 'Sin cebolla en la pizza'
        },
        {
          id: '2',
          tableNumber: '3',
          customerName: 'Ana López',
          items: [
            { id: '3', name: 'Pasta Carbonara', quantity: 1, price: 15.99 }
          ],
          totalAmount: 15.99,
          status: 'ready',
          createdAt: new Date(Date.now() - 30 * 60 * 1000).toISOString()
        }
      ];
      
      console.log('📋 Órdenes cargadas:', mockOrders);
      setOrders(mockOrders);
    } catch (error) {
      console.error('❌ Error cargando órdenes:', error);
      if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
        setErrorModal({ open: true, title: 'No se pudieron cargar las órdenes', message: error.message });
      } else {
        setErrorModal({ open: true, title: 'Error del servidor', message: 'No se pudieron cargar las órdenes' });
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: Order['status']) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'preparing': return 'bg-blue-100 text-blue-800';
      case 'ready': return 'bg-green-100 text-green-800';
      case 'delivered': return 'bg-gray-100 text-gray-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: Order['status']) => {
    switch (status) {
      case 'pending': return 'Pendiente';
      case 'preparing': return 'Preparando';
      case 'ready': return 'Listo';
      case 'delivered': return 'Entregado';
      case 'cancelled': return 'Cancelado';
      default: return status;
    }
  };

  const getStatusIcon = (status: Order['status']) => {
    switch (status) {
      case 'pending': return '⏳';
      case 'preparing': return '👨‍🍳';
      case 'ready': return '✅';
      case 'delivered': return '🎉';
      case 'cancelled': return '❌';
      default: return '📋';
    }
  };

  const filteredOrders = orders.filter(order => 
    selectedStatus === 'all' || order.status === selectedStatus
  );

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('es-ES', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const formatPrice = (price: number) => {
    return `$${price.toFixed(2)}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#e9ecef] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Cargando órdenes...</span>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#e9ecef] p-6">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-3xl font-bold text-gray-900">
              Órdenes
            </h1>
            <div className="flex space-x-2">
              <button 
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedStatus === 'all' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                onClick={() => setSelectedStatus('all')}
              >
                Todas
              </button>
              <button 
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedStatus === 'pending' 
                    ? 'bg-yellow-600 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                onClick={() => setSelectedStatus('pending')}
              >
                Pendientes
              </button>
              <button 
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedStatus === 'ready' 
                    ? 'bg-green-600 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                onClick={() => setSelectedStatus('ready')}
              >
                Listas
              </button>
            </div>
          </div>

          {filteredOrders.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">📋</div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                No hay órdenes
              </h2>
              <p className="text-gray-600">
                {selectedStatus === 'all' 
                  ? 'No hay órdenes en el sistema' 
                  : `No hay órdenes ${getStatusText(selectedStatus as Order['status']).toLowerCase()}`
                }
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredOrders.map((order) => {
                const isOldOrder = (Date.now() - new Date(order.createdAt).getTime()) > 15 * 60 * 1000;
                const minutesWaiting = Math.floor((Date.now() - new Date(order.createdAt).getTime()) / 60000);
                
                return (
                  <div key={order.id} className={`bg-white border rounded-lg shadow-sm hover:shadow-md transition-shadow ${
                    isOldOrder ? 'border-yellow-300 bg-yellow-50' : 'border-gray-200'
                  }`}>
                    <div className="p-6">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">
                            Orden #{order.id}
                          </h3>
                          <p className="text-sm text-gray-600">
                            {formatTime(order.createdAt)}
                          </p>
                        </div>
                        <div className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(order.status)}`}>
                          <span className="mr-1">{getStatusIcon(order.status)}</span>
                          {getStatusText(order.status)}
                        </div>
                      </div>

                      {isOldOrder && (
                        <div className="bg-yellow-100 text-yellow-800 px-3 py-2 rounded-lg mb-4 text-sm font-medium">
                          ⏰ {minutesWaiting} min esperando
                        </div>
                      )}

                      <div className="space-y-1 mb-4">
                        {order.items.slice(0, 3).map((item, index) => (
                          <div key={index} className="text-sm text-gray-700">
                            <span className="font-medium">{item.quantity}x</span> {item.name}
                            {item.specialInstructions && (
                              <span className="text-gray-500 italic"> ({item.specialInstructions})</span>
                            )}
                          </div>
                        ))}
                        {order.items.length > 3 && (
                          <div className="text-sm text-gray-500">
                            +{order.items.length - 3} más...
                          </div>
                        )}
                      </div>

                      <div className="flex justify-between items-center">
                        <div className="text-lg font-bold text-gray-900">
                          {formatPrice(order.totalAmount)}
                        </div>
                        <div className="flex space-x-2">
                          <button className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                            👁️
                          </button>
                          <button className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors">
                            ✅
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <ErrorModal isOpen={errorModal.open} title={errorModal.title} message={errorModal.message} details={errorModal.details} onClose={() => setErrorModal({ open: false, message: '' })} />
    </div>
  );
} 